import {Component} from 'react';
import h from 'react-hyperscript';
import './Messaging.scss';
import BasicCard from 'Components/Cards/BasicCard';
import {h4, h3, p} from 'Components/Typography/Typography';
import Button from 'Components/Buttons/Buttons';
import {ENDPOINTS} from 'Src/utils/utils';
import ShareLink from 'Components/ShareLink/ShareLink';
import surveyContent from 'Assets/data/survey.json';
import ErrorMessage from 'Components/ErrorMessage/ErrorMessage';
import {
  getAvg,
  formatDateShort,
  replaceReportAvailableDatePlaceholder} from 'Src/utils/utils';
import BarChartHorizontal from
  'Components/DataViz/BarCharts/BarChartHorizontal';

class Messaging extends Component {
  constructor(props) {
    super(props);
    this._isMounted = false;
    this.largeViewportGraphSize = 920;

    this.state = {
      mountedClass: 'fade-out',
      isAdmin: Boolean(this.props.userStatus.admin_level === 'Admin'),
      isSurveyOpen: Boolean(this.props.userStatus.survey_open_period_flag),
      surveyUrl: '',
      graphContainerWidth: this.largeViewportGraphSize,
    };

    this.setRef = this.setRef.bind(this);
  }

  componentDidMount() {
    this._isMounted = true;
    this.setMountedClass();
    this.checkIfAdmin();
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.setMountedClass();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.screenWidth !== this.props.screenWidth && this.graphRef) {
      this.setGraphContainerWidth();
    }

    if (this.props.userStatus !== prevProps.userStatus) {
      this.checkIfAdmin();
    }
  }

  sanitize(string) {
    const date = new Date(
        this.props.userStatus.dates.survey_close_date.replace('-', '/')
    );
    return replaceReportAvailableDatePlaceholder(string, date);
  }

  setSurveyStatus(data) {
    if (this._isMounted) {
      this.setState({
        surveyUrl: data.share_survey_url,
      });
    }
  }

  setIsAdmin(value) {
    this.setState({
      isAdmin: value,
    });
  }

  setIsSurveyOpen(value) {
    this.setState({
      isSurveyOpen: value,
    });
  }

  setMountedClass() {
    let value;
    if (this._isMounted) {
      value = 'fade-in';
    } else {
      value = 'fade-out';
    }
    this.setState({
      mountedClass: value,
    });
  }

  setRef(ref) {
    this.graphRef = ref;

    this.setGraphContainerWidth();
  }

  checkIfAdmin() {
    if (this.props.userStatus.admin_level === 'Admin') {
      this.setIsAdmin(true);
      fetch(ENDPOINTS.SURVEY_STATUS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': this.props.idToken,
          'X-CSRF-Token': this.props.csrf,
        },
        body: JSON.stringify({
          access_token: this.props.googleUser,
        }),
      })
          .then((response) => response.json())
          .then((result) => {
            if (result.data) {
              this.setSurveyStatus(result.data);
            } else {
              console.log('Result needs attention: ', result);
            }
          }, (error) => {
            console.log('No result', error);
          });
    } else {
      this.setIsAdmin(false);
    }

    if (this.props.userStatus.survey_open_period_flag) {
      this.setIsSurveyOpen(true);
    } else {
      this.setIsSurveyOpen(false);
    }
  }

  setGraphContainerWidth() {
    const graphContainerWidth = this.graphRef.current.offsetWidth;
    if (
      this.state.graphContainerWidth !== graphContainerWidth &&
      this.props.dynamicGraphSize
    ) {
      this.setState({
        graphContainerWidth,
      });
    } else if (this.state.graphContainerWidth) {
      this.setState({
        graphContainerWidth: this.largeViewportGraphSize,
      });
    }
  }

  createReviewBars() {
    const reviewBars = this.props.transformationElements.map((element, i) => {
      const id = `${element.id}Values`;
      let barValue;
      let barTitle;
      const elementProgressValues = [];

      if (this.props.fieldValues[id]) {
        const values = this.props.fieldValues[id].surveyValues;
        values.progress.forEach((value, i) => {
          if (value.type === 'AVG') {
            elementProgressValues.push(Number(value.value));
          }
        });
        barValue = getAvg(elementProgressValues);
        barTitle = element.title;
      }

      const reviewBar = {
        name: barTitle,
        value: barValue,
        valueFormatted: `${Math.floor(barValue * 100)}%`,
      };
      return reviewBar;
    });

    return reviewBars;
  }

  get descElement() {
    const desc = this.props.content.desc;
    const isClosed = this.props.userStatus.survey_open_period_closed;

    return h(p, {
      className: 'text--grey',
      id: 'survey-complete__desc',
    }, [
      desc.partOne.replace(
          '[survey_open_period_closed]', formatDateShort(isClosed)
      ),
      ' ',
      h('a', {
        href: desc.partTwo.href,
        alt: desc.partTwo.alt,
        target: '_blank',
      }, desc.partTwo.text),
      ' ',
      this.sanitize(desc.partThree),
    ]);
  }

  createChildren() {
    let title = this.props.content.title;
    let desc = this.props.content.desc;
    const surveyClosed = !this.state.isSurveyOpen;

    if (surveyClosed) {
      title = surveyContent.surveyClosed.title;
      desc = surveyContent.surveyClosed.desc;
    }
    const barChartData = this.createReviewBars();
    const isBarChartValid =
      barChartData && barChartData.length && barChartData[0].name;
    const children = [
      h(h3, null, title),
      surveyClosed ? h(p, {
        className: 'text--grey',
        id: 'survey-complete__desc',
      }, desc) : this.descElement,
      isBarChartValid ? h(h4, {
        key: 'review-subtitle__key',
        className: 'padding-bottom--md padding-top--lg',
      }, this.props.content.subtitle) : null,
      isBarChartValid ? h(BarChartHorizontal, {
        key: 'review-bar-chart__key',
        id: 'review-bar-chart',
        data: barChartData,
        barHeight: 20,
        color: '#1a73e8',
        size: [this.state.graphContainerWidth, 357],
        setRef: this.setRef,
        dynamicGraphSize: this.props.dynamicGraphSize,
      }, null) : null,
    ];

    if (this.state.isAdmin && this.state.isSurveyOpen) {
      children.push(
          h('div', {
            className: 'flex flex--center margin-top--lg-2',
          }, [
            h(Button, {
              id: 'survey-complete__cta',
              key: 'survey-complete__cta',
              to: '/app/',
              className: 'button--centered survey__cta',
              type: 'basic',
              buttonClasses: 'button--full-height',
            }, this.props.content.cta),
            h(ShareLink, {
              id: `survey-complete__share-link`,
              slug: 'survey-complete__share-link',
              message: 'Share link: Invite others on your ' +
                'leadership team to take the survey',
              shareUrl: this.state.surveyUrl,
              buttonClasses: 'button--full-width',
            }, null),
          ])
      );
    }
    return children;
  }

  render() {
    if (this.props.errorMessage) {
      return (
        h(ErrorMessage, {
          message: this.props.errorMessage,
        }, null)
      );
    } else {
      return (
        h(BasicCard, {
          className: `${this.props.className} ${this.state.mountedClass}`,
          activeStep: this.props.activeStep,
        }, [
          h('div', {
            id: 'survey-complete__header',
          }, this.createChildren()),
        ])
      );
    }
  }
}

export default Messaging;
